/**
 * @fileoverview Carry an invitation through sign-in.
 *
 * An invitation email is the only thing its recipient has, and the page it
 * lands on is signed out. Three things have to survive that gap:
 *
 * 1. **Which invitation it was**, so the sign-in form can say whose it is
 *    rather than looking like a mis-sent email.
 * 2. **That they have no account yet**, so the form opens in Sign Up mode —
 *    the rule is that a stranger is PROMPTED to create one, not dropped on a
 *    sign-in form they cannot use.
 * 3. **Where to go once they are in** — the Invites tab for a program
 *    invitation, Settings for an organization one. "Once they are in" means
 *    once they can REACH it: a new account owes onboarding first, so for them
 *    the stash waits for onboarding's last step (see `inviteLanding`).
 *
 * sessionStorage, not the URL, because the Google round trip leaves Hans
 * entirely and comes back to the origin with only OAuth's own parameters. The
 * same stash is therefore read by the login page, the signup path, the OAuth
 * callback and onboarding alike. It is the pattern `hans_tos_accepted` already
 * uses.
 *
 * Every read and write is wrapped: a browser with storage disabled must still
 * be able to sign in. Losing the destination costs one navigation; throwing
 * costs the whole login.
 */

import { programsPath } from "@/lib/programsRoute";

const KEY = "hans_pending_invite";

/** What the invitation email put in the URL, if anything. */
export function inviteFromUrl(search = window.location.search) {
  const params = new URLSearchParams(search);
  const inviteId = params.get("invite");
  const orgInviteToken = params.get("org_invite");
  if (!inviteId && !orgInviteToken) return null;
  return { inviteId, orgInviteToken, signup: params.get("signup") === "1" };
}

/** Hold it across the sign-in (and across Google's round trip). */
export function stashInvite(invite) {
  if (!invite) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(invite));
  } catch {
    /* Storage disabled. The invitation still exists; only the hand-off is lost. */
  }
}

/** Read it back and clear it — it is for exactly one landing. */
export function consumeInvite() {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Read it back WITHOUT clearing it — for a page that has to say where its
 * button goes before anybody presses it (onboarding's last step). Saying so
 * must not use the invitation up, and a refresh must find it still there.
 */
export function peekInvite() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Where a just-signed-in instructor should land, or null for the usual place.
 *
 * A PROGRAM invitation goes to the Invites tab, which is where the email said
 * it would be. An ORGANIZATION invitation goes to Settings, which is where its
 * accept/decline banner lives — a different object with a different answer,
 * and sending it to Programs would be a tour of somewhere it isn't.
 */
export function inviteDestination(invite, user) {
  if (!invite) return null;
  if (invite.inviteId) return `${programsPath(user)}?tab=invites`;
  if (invite.orgInviteToken) return "/settings";
  return null;
}

/**
 * Where sign-in should land this account for its invitation, or null for the
 * usual place — spending the stash only when the answer is the invitation.
 *
 * An account that has not finished onboarding cannot land anywhere yet:
 * AuthProvider turns it to /onboarding whatever URL it was given. Spending the
 * stash on that navigation is how the invitation that made a stranger sign up
 * was lost a screen before it paid off, leaving them to go looking for it from
 * the dashboard. So it is left where it is, and onboarding's last step spends
 * it instead (see OnboardingPage).
 *
 * The test is AuthProvider's own, falsy rather than `=== false`, and must stay
 * that way: a brand-new Google account's payload carries no
 * `onboarding_completed` at all, and a stricter test would read it as done.
 */
export function inviteLanding(user) {
  if (!user?.onboarding_completed) return null;
  return inviteDestination(consumeInvite(), user);
}
